import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    Expired,
    Match,
    MissingKeyAlias,
    PGPApi,
    PGPKeyDetails,
    PGPKeyringApi,
    PGPKeyServerClientApi,
    PGPKeyType,
    PGPSearchField,
    PGPTrustListApi,
    PGPTrustListStatus
} from "components/src/api/admin/generated";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {
    Bin as DeleteIcon,
    Check as TrustIcon,
    DatabaseExport as ExportIcon,
    Import as ImportKeyringIcon,
    Key as PGPIcon,
    Plus as CreateNewKeyIcon,
    Prohibition as RevokeIcon,
    Refresh as RefreshIcon,
    ShareIos as SubmitIcon,
    ShieldSearch as FindKeysIcon,
    Xmark as DistrustIcon
} from "iconoir-react";
import {useCallback, useEffect, useState} from "react";
import {Dropdown} from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useLocation, useNavigate} from "react-router-dom";
import {PGPCreateNewKeyDialog} from "./PGPCreateNewKeyDialog";
import {PGPKeyDetailsDialogs} from "./PGPKeyDetailsDialog";
import {PGPKeyFilter} from "./PGPKeyFilter";
import {PGPKeyRingExportDialog} from "./PGPKeyRingExportDialog";
import {PGPKeyRingImportDialog} from "./PGPKeyRingImportDialog";
import {PGPKeyRow} from "./PGPKeyRow";
import {PGPKeyServerDialog} from "./PGPKeyServerDialog";
import {PGPKeyTableHeader} from "./PGPKeyTableHeader";

/*
 * REST api for accessing the back-end
 */
const pgpKeyringApi = new PGPKeyringApi(ApiConfig.config());
const pgpTrustListApi = new PGPTrustListApi(ApiConfig.config());
const pgpKeyServerClientApi = new PGPKeyServerClientApi(ApiConfig.config());
const pgpApi = new PGPApi(ApiConfig.config());

export const PGPKeyRing = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * Represents the current location object in a React Router application.
     */
    const location = useLocation()
    /**
     * The loaded PGP keys
     */
    const [keys, setKeys] = useState<PGPKeyDetails[]>([]);
    /**
     * The total number of keys
     */
    const [keyCount, setKeyCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of keys to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected keys
     */
    const [selectedKeys, setSelectedKeys] = useState<PGPKeyDetails[]>([]);
    /**
     * The key for which the details will be shown
     */
    const [key, setKey] = useState<PGPKeyDetails>();
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "confirm refresh keys" modal dialog will be shown
     */
    const [showRefreshKeysConfirm, setShowRefreshKeysConfirm] = useState<boolean>(false);
    /**
     * If true, the "confirm revoke keys" modal dialog will be shown
     */
    const [showRevokeKeysConfirm, setShowRevokeKeysConfirm] = useState<boolean>(false);
    /**
     * If true, the "confirm submit keys" modal dialog will be shown
     */
    const [showSubmitKeysConfirm, setShowSubmitKeysConfirm] = useState<boolean>(false);
    /**
     * If true, the import Keyring dialog will be shown
     */
    const [showKeyRingImportDialog, setShowKeyRingImportDialog] = useState<boolean>(false);
    /**
     * If true, the export Keyring dialog will be shown
     */
    const [showKeyRingExportDialog, setShowKeyRingExportDialog] = useState<boolean>(false);
    /**
     * If true, the Key Server dialog will be shown
     */
    const [showKeyServerDialog, setShowKeyServerDialog] = useState<boolean>(false);
    /**
     * If true, the Key details dialog will be shown
     */
    const [showKeyDetailsDialog, setShowKeyDetailsDialog] = useState<boolean>(false);
    /**
     * If true, the create new key dialog will be shown
     */
    const [showCreateNewKeyDialog, setShowCreateNewKeyDialog] = useState<boolean>(false);
    /**
     * Determines whether expired keys are shown
     */
    const [expired, setExpired] = useState<Expired>(location?.state?.expired??Expired.All);
    /**
     * Determines whether keys without private keys are shown
     */
    const [missingKeyAlias, setMissingKeyAlias] = useState<MissingKeyAlias>(location?.state?.missingKeyAlias??MissingKeyAlias.Allowed);
    /**
     * Determines whether only master keys and/or sub keys will be shown
     */
    const [keyType, setKeyType] = useState<PGPKeyType>(PGPKeyType.MasterKey);
    /**
     * The field to search on
     */
    const [searchField, setSearchField] = useState<PGPSearchField>(PGPSearchField.Email);
    /**
     * The field which is searched on
     */
    const [activeSearchValue, setActiveSearchValue] = useState<string>();
    /**
     * The field of the search box. This value will be copied to the activeSearchValue when apply is clicked
     */
    const [searchValue, setSearchValue] = useState<string>();
    /**
     * Incremented whenever the data should be reloaded from the back-end. A counter is used instead of a boolean
     * because every increment is a new value, so a reload is never dropped and the load effects run exactly once
     * per reload request
     */
    const [reloadCounter, setReloadCounter] = useState<number>(0);
    /**
     * Forces the data to be reloaded from the back-end
     */
    const forceReload = useCallback(() => setReloadCounter(counter => counter + 1), []);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() => {
        logger.debug("Getting PGP key count")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        let loadCallback;

        if ((activeSearchValue??"").length > 0)
        {
            loadCallback = async () => {
                return await pgpKeyringApi.pGPKeyringControllerSearchKeysCount(`%${activeSearchValue??""}%`,
                    searchField, Match.Like, expired, missingKeyAlias, keyType);
            };
        }
        else {
            loadCallback = async () => {
                return await pgpKeyringApi.pGPKeyringControllerGetKeysCount(expired, missingKeyAlias, keyType);
            };
        }

        loadCallback()
            .then(response => {
                if (superseded) {
                    return;
                }

                setKeyCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting certificates count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [expired,
        missingKeyAlias,
        keyType,
        activeSearchValue,
        searchField,
        navigate,
        reloadCounter,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        logger.debug("Getting PGP keys")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        // reset state when we load new data
        setSelectedKeys([]);
        // Note: do not reset errorMessage because otherwise the message might get lost when re-rendering
        //       The error message can be closed by clicking the close icon

        let loadCallback;

        if ((activeSearchValue??"").length > 0)
        {
            loadCallback = async () => {
                return await pgpKeyringApi.pGPKeyringControllerSearchKeys(`%${activeSearchValue??""}%`,
                    searchField, Match.Like, expired, missingKeyAlias, keyType,
                    (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }
        else {
            loadCallback = async () => {
                return await pgpKeyringApi.pGPKeyringControllerGetKeys(expired, missingKeyAlias, keyType,
                    (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }

        loadCallback().then(response => {
            if (superseded) {
                return;
            }

            setKeys(response.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [expired,
        missingKeyAlias,
        keyType,
        activeSearchValue,
        searchField,
        navigate,
        reloadCounter,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    /**
     * Deletes selected keys. This will be called when the delete button of the "delete confirm" dialog is clicked.
     */
    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyringApi.pGPKeyringControllerDeleteKeys(selectedKeys.map(k => k.id))
            .then(() => {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting PGP keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowDeleteConfirm(false);
            setSelectedKeys([]);
        })
    }

    /**
     * Refresh selected keys. This will be called when the refresh button of the "download confirm" dialog is clicked.
     */
    const refreshSelectedKeys = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyServerClientApi.pGPKeyServerClientControllerRefreshKeys(selectedKeys.map(k => k.id))
            .then(() => {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error refreshing PGP keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowRefreshKeysConfirm(false);
            setSelectedKeys([]);
        })
    }

    /**
     * Revoke selected secret keys. This will be called when the revoke button of the "revoke confirm" dialog is clicked.
     */
    const revokeSelectedKeys = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpApi.pGPControllerRevokeKeys(selectedKeys.map(k => k.id))
            .then(() => {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error refreshing PGP keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowRevokeKeysConfirm(false);
            setSelectedKeys([]);
        })
    }


    /**
     * submit selected keys to a remote PGP keyserver
     */
    const submitSelectedKeys = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyServerClientApi.pGPKeyServerClientControllerSubmitKeys(selectedKeys.map(k => k.id))
            .then(() => {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error submitting PGP keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowSubmitKeysConfirm(false);
            setSelectedKeys([]);
        })
    }

    /**
     * Called when a key is selected (i.e., when the checkbox is checked)
     */
    const onSelectKey = (checked : boolean, key : PGPKeyDetails) =>
    {
        setSelectedKeys(checked ?
            [...selectedKeys, key] :
            selectedKeys.filter(e => e.fingerprint !== key.fingerprint)
        );
    }

    /**
     * Inverts the selection of keys by updating the list of selected keys.
     * Each previously unselected key will be selected, and each previously
     * selected key will be unselected.
     */
    const invertSelection = () => {
        setSelectedKeys([...keys].filter(cert => !selectedKeys.includes(cert)));
    }

    /**
     * Is called when the PGPKeyRingImportDialog is closed
     */
    const onKeyRingImportDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowKeyRingImportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Is called when the PGPKeyRingExportDialog is closed
     */
    const onKeyRingExportDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowKeyRingExportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            // clear the selected keys after export
            setSelectedKeys([]);
        }
    }

    /**
     * Is called when the PGPKeyServerDialog is closed
     */
    const onKeyServerDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowKeyServerDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Is called when the PGPKeyDetailsDialog is closed
     */
    const onKeyDetailsDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowKeyDetailsDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Is called when the PGPCreateNewKeyDialog is closed
     */
    const onCreateNewKeyDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowCreateNewKeyDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Sets the trust status of selected PGP keys.
     */
    const setTrustStatusOfSelectedKeys = (trustStatus : PGPTrustListStatus) =>
    {
        pgpTrustListApi.pGPTrustListControllerSetPGPTrustListEntries(trustStatus,
            selectedKeys.map(k => k.sha256Fingerprint), true)
            .then(() =>
            {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error trusting keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            setSelectedKeys([]);
        })
    }

    function onClickKeyId(key : PGPKeyDetails)
    {
        setKey(key);
        setShowKeyDetailsDialog(true);
    }

    return (
        <>
            <ConfirmDialog title= {t("pgp-key-ring.confirm")}
                           bodyText={t("pgp-key-ring.sure-delete-selected")}
                           confirmButtonTitle="Delete"
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            <ConfirmDialog title= {t("pgp-key-ring.confirm")}
                           bodyText={t("pgp-key-ring.sure-refresh-selected")}
                           confirmButtonTitle="Refresh"
                           onClickConfirmEvent={refreshSelectedKeys}
                           showConfirm={showRefreshKeysConfirm}
                           setShowConfirm={setShowRefreshKeysConfirm}
                           buttonVariant="success"
            />

            <ConfirmDialog title= {t("pgp-key-ring.confirm")}
                           bodyText={t("pgp-key-ring.sure-revoke-secret")}
                           confirmButtonTitle="Revoke"
                           onClickConfirmEvent={revokeSelectedKeys}
                           showConfirm={showRevokeKeysConfirm}
                           setShowConfirm={setShowRevokeKeysConfirm}
            />

            <ConfirmDialog title= {t("pgp-key-ring.confirm")}
                           bodyText={t("pgp-key-ring.sure-submit-keys")}
                           confirmButtonTitle="Submit"
                           onClickConfirmEvent={submitSelectedKeys}
                           showConfirm={showSubmitKeysConfirm}
                           setShowConfirm={setShowSubmitKeysConfirm}
            />

            {showKeyRingImportDialog && <PGPKeyRingImportDialog onClose={onKeyRingImportDialogClose} />}
            {showKeyRingExportDialog && <PGPKeyRingExportDialog keys={selectedKeys} onClose={onKeyRingExportDialogClose} />}
            {showKeyServerDialog && <PGPKeyServerDialog onClose={onKeyServerDialogClose} />}
            {showKeyDetailsDialog && key !== undefined && <PGPKeyDetailsDialogs pgpKey={key} onClose={onKeyDetailsDialogClose} />}
            {showCreateNewKeyDialog && <PGPCreateNewKeyDialog onClose={onCreateNewKeyDialogClose} />}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<PGPIcon />} title={t("pgp.title")} onClickReload={forceReload}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <div className="d-xl-flex gap-2">
                                    <PGPKeyFilter expired={expired}
                                                  setExpired={setExpired}
                                                  missingKeyAlias={missingKeyAlias}
                                                  setMissingKeyAlias={setMissingKeyAlias}
                                                  keyType={keyType}
                                                  setKeyType={setKeyType}
                                                  searchField={searchField}
                                                  setSearchField={setSearchField}
                                                  searchValue={searchValue}
                                                  setSearchValue={setSearchValue}
                                                  activeSearchValue={activeSearchValue}
                                                  setActiveSearchValue={setActiveSearchValue}
                                    />
                                </div>

                                <div className="ms-4"/>

                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => setShowKeyRingImportDialog(true)}>
                                        <ImportKeyringIcon className="text-success"/> {t("pgp-key-ring.import-keyring")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setShowKeyServerDialog(true)}>
                                        <FindKeysIcon className="text-success"/> {t("pgp-key-ring.find-keys")}
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={() => setShowCreateNewKeyDialog(true)}>
                                        <CreateNewKeyIcon className="text-success"/> {t("pgp-key-ring.create-new-key")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                {selectedKeys.length === 0 ?
                                    <PGPKeyTableHeader>
                                        {/* Add icon to first column which will invert selection when clicked */}
                                        <InvertSelection invertSelectionEvent={invertSelection}/>
                                    </PGPKeyTableHeader>
                                    :
                                    <thead className="sticky-top">
                                    <tr className="text-nowrap">
                                        <th colSpan={20}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedKeys([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedKeys.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger" /> {t("pgp-key-ring.delete-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => setShowKeyRingExportDialog(true)}>
                                                        <ExportIcon className="text-success"/> {t("pgp-key-ring.export-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => setTrustStatusOfSelectedKeys(PGPTrustListStatus.Trusted)}>
                                                        <TrustIcon className="text-success"/> {t("pgp-key-ring.trust-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => setTrustStatusOfSelectedKeys(PGPTrustListStatus.Untrusted)}>
                                                        <DistrustIcon className="text-success"/> {t("pgp-key-ring.distrust-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => setShowRefreshKeysConfirm(true)}>
                                                        <RefreshIcon className="text-success"/> {t("pgp-key-ring.refresh-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => setShowRevokeKeysConfirm(true)}>
                                                        <RevokeIcon className="text-danger" /> {t("pgp-key-ring.revoke-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => setShowSubmitKeysConfirm(true)}>
                                                        <SubmitIcon className="text-success" /> {t("pgp-key-ring.submit-selected")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    </tr>
                                    </thead>
                                }
                                <tbody>
                                {keys.map((key : PGPKeyDetails) => (
                                    <PGPKeyRow key={key.fingerprint} pgpKey={key} onClickKey={() => onClickKeyId(key)}>
                                        {/* Add selection checkbox to first column */}
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={selectedKeys.find((skey) =>  skey.fingerprint === key.fingerprint) !== undefined}
                                            onChange={(e) => onSelectKey(e.target.checked, key)}
                                            inline
                                        />
                                    </PGPKeyRow>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={keyCount ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
}
